import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  HydratedOrganizationFile,
  AddOrganizationFilesParams,
  CreateOrganizationFolderParams,
  ImportOrganizationFileZipParams,
  ImportOrganizationFileZipResponse,
  DeleteOrganizationFilesParams,
  UpdateOrganizationFileParams,
  ListOrganizationFilesResponseSchema,
  AddOrganizationFilesResponseSchema,
  CreateOrganizationFolderResponseSchema,
  ImportOrganizationFileZipResponseSchema,
  UpdateOrganizationFileResponseSchema,
  OrganizationFileBreadcrumb,
  SearchOrganizationFileImportKeysParams,
  SearchOrganizationFileImportKeysResponse,
  SearchOrganizationFileImportKeysResponseSchema,
} from "@forgepoint/shared";

interface FileLoadingState {
  isLoadingDelete: boolean;
  deleteError: string | null;
  isLoadingUpdate: boolean;
  updateError: string | null;
}

const getInitialLoadingState = (): FileLoadingState => ({
  isLoadingDelete: false,
  deleteError: null,
  isLoadingUpdate: false,
  updateError: null,
});

interface FetchFilesParams {
  search?: string;
  parentFolderId?: string | null;
  /** List file entries only — folders excluded, spanning all folders. */
  excludeFolders?: boolean;
  limit?: number;
  offset?: number;
}

/** Caps a picker listing; folders sort first, so they stay complete. */
const PICKER_LIST_LIMIT = 200;

const buildListQuery = (params?: FetchFilesParams): string => {
  const searchParams = new URLSearchParams();
  if (params?.search) {
    searchParams.set("search", params.search);
  }
  if (params?.parentFolderId) {
    searchParams.set("parentFolderId", params.parentFolderId);
  }
  if (params?.excludeFolders) {
    searchParams.set("excludeFolders", "true");
  }
  if (params?.limit !== undefined) {
    searchParams.set("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    searchParams.set("offset", String(params.offset));
  }
  return searchParams.toString();
};

interface FilesStore {
  files: Map<string, HydratedOrganizationFile>;
  fileLoadingStates: Map<string, FileLoadingState>;
  listLoading: boolean;
  listError: string | null;
  total: number;
  currentFolder: HydratedOrganizationFile | null;
  breadcrumbs: OrganizationFileBreadcrumb[];

  fetchFiles: (params?: FetchFilesParams) => Promise<string[]>;
  /**
   * Lists the contents of a folder (null for the vault root) for pickers,
   * upserting the entries into the files map without touching the main
   * browser's list state. The listing is capped at `PICKER_LIST_LIMIT`;
   * `total` reports the uncapped count.
   */
  fetchFolderListing: (parentFolderId: string | null) => Promise<{
    entryIds: string[];
    breadcrumbs: OrganizationFileBreadcrumb[];
    total: number;
  } | null>;
  searchImportKeys: (
    params: SearchOrganizationFileImportKeysParams,
  ) => Promise<SearchOrganizationFileImportKeysResponse | null>;
  addFiles: (
    params: AddOrganizationFilesParams,
  ) => Promise<HydratedOrganizationFile[] | null>;
  createFolder: (
    params: CreateOrganizationFolderParams,
  ) => Promise<HydratedOrganizationFile | null>;
  importZip: (
    params: ImportOrganizationFileZipParams,
  ) => Promise<ImportOrganizationFileZipResponse | null>;
  updateFile: (
    params: UpdateOrganizationFileParams,
  ) => Promise<HydratedOrganizationFile | null>;
  deleteFiles: (params: DeleteOrganizationFilesParams) => Promise<boolean>;
}

export const useFilesStore = create<FilesStore>((set) => ({
  files: new Map(),
  fileLoadingStates: new Map(),
  listLoading: false,
  listError: null,
  total: 0,
  currentFolder: null,
  breadcrumbs: [],

  fetchFiles: async (params?: FetchFilesParams) => {
    set({ listLoading: true, listError: null });

    try {
      const queryString = buildListQuery(params);
      const url = queryString
        ? `organization-files?${queryString}`
        : "organization-files";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = ListOrganizationFilesResponseSchema.parse(response.data);
        set((state) => {
          const filesMap = new Map(state.files);
          data.files.forEach((file) => {
            filesMap.set(file.id, file);
          });
          return {
            files: filesMap,
            listLoading: false,
            total: data.total,
            currentFolder: data.currentFolder,
            breadcrumbs: data.breadcrumbs,
          };
        });
        return data.files.map((f) => f.id);
      }

      set({ listError: "Failed to fetch files", listLoading: false });
      return [];
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch files", listLoading: false });
      return [];
    }
  },

  fetchFolderListing: async (parentFolderId: string | null) => {
    try {
      const queryString = buildListQuery({
        parentFolderId,
        limit: PICKER_LIST_LIMIT,
      });
      const response = await fetchApi(
        `organization-files?${queryString}`,
        "GET",
      );

      if (!response.ok || !response.data) {
        return null;
      }

      const data = ListOrganizationFilesResponseSchema.parse(response.data);
      set((state) => {
        const filesMap = new Map(state.files);
        data.files.forEach((entry) => {
          filesMap.set(entry.id, entry);
        });
        return { files: filesMap };
      });
      return {
        entryIds: data.files.map((entry) => entry.id),
        breadcrumbs: data.breadcrumbs,
        total: data.total,
      };
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  searchImportKeys: async (params: SearchOrganizationFileImportKeysParams) => {
    try {
      const response = await fetchApi(
        "organization-files/import-keys/search",
        "POST",
        { body: params },
      );

      if (!response.ok) {
        return null;
      }
      return SearchOrganizationFileImportKeysResponseSchema.parse(
        response.data,
      );
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  addFiles: async (params: AddOrganizationFilesParams) => {
    try {
      const response = await fetchApi("organization-files", "POST", {
        body: params,
      });

      if (!response.ok) {
        return null;
      }

      const parsed = AddOrganizationFilesResponseSchema.safeParse(
        response.data,
      );
      if (!parsed.success) {
        captureException(parsed.error);
        return null;
      }

      set((state) => {
        const filesMap = new Map(state.files);
        parsed.data.files.forEach((file) => {
          filesMap.set(file.id, file);
        });
        return { files: filesMap };
      });
      return parsed.data.files;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  createFolder: async (params: CreateOrganizationFolderParams) => {
    try {
      const response = await fetchApi("organization-files/folders", "POST", {
        body: params,
      });

      if (response.ok && response.data) {
        const result = CreateOrganizationFolderResponseSchema.parse(
          response.data,
        );
        set((state) => {
          const filesMap = new Map(state.files);
          filesMap.set(result.folder.id, result.folder);
          return { files: filesMap };
        });
        return result.folder;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  importZip: async (params: ImportOrganizationFileZipParams) => {
    try {
      const response = await fetchApi("organization-files/import-zip", "POST", {
        body: params,
      });

      if (response.ok && response.data) {
        const result = ImportOrganizationFileZipResponseSchema.parse(
          response.data,
        );
        return result;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  updateFile: async (params: UpdateOrganizationFileParams) => {
    set((state) => {
      const loadingStates = new Map(state.fileLoadingStates);
      const currentState =
        loadingStates.get(params.organizationFileId) ||
        getInitialLoadingState();
      loadingStates.set(params.organizationFileId, {
        ...currentState,
        isLoadingUpdate: true,
        updateError: null,
      });
      return { fileLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi("organization-files", "PATCH", {
        body: params,
      });

      if (response.ok && response.data) {
        const result = UpdateOrganizationFileResponseSchema.parse(
          response.data,
        );
        set((state) => {
          const filesMap = new Map(state.files);
          const loadingStates = new Map(state.fileLoadingStates);
          const currentState =
            loadingStates.get(params.organizationFileId) ||
            getInitialLoadingState();
          filesMap.set(result.file.id, result.file);
          loadingStates.set(params.organizationFileId, {
            ...currentState,
            isLoadingUpdate: false,
            updateError: null,
          });
          return { files: filesMap, fileLoadingStates: loadingStates };
        });
        return result.file;
      }

      set((state) => {
        const loadingStates = new Map(state.fileLoadingStates);
        const currentState =
          loadingStates.get(params.organizationFileId) ||
          getInitialLoadingState();
        loadingStates.set(params.organizationFileId, {
          ...currentState,
          isLoadingUpdate: false,
          updateError: "Failed to update file",
        });
        return { fileLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.fileLoadingStates);
        const currentState =
          loadingStates.get(params.organizationFileId) ||
          getInitialLoadingState();
        loadingStates.set(params.organizationFileId, {
          ...currentState,
          isLoadingUpdate: false,
          updateError: "Failed to update file",
        });
        return { fileLoadingStates: loadingStates };
      });
      return null;
    }
  },

  deleteFiles: async (params: DeleteOrganizationFilesParams) => {
    set((state) => {
      const loadingStates = new Map(state.fileLoadingStates);
      for (const id of params.organizationFileIds) {
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: true,
          deleteError: null,
        });
      }
      return { fileLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi("organization-files", "DELETE", {
        body: params,
      });

      if (response.ok) {
        set((state) => {
          const filesMap = new Map(state.files);
          const loadingStates = new Map(state.fileLoadingStates);
          for (const id of params.organizationFileIds) {
            filesMap.delete(id);
            loadingStates.delete(id);
          }
          return { files: filesMap, fileLoadingStates: loadingStates };
        });
        return true;
      }

      set((state) => {
        const loadingStates = new Map(state.fileLoadingStates);
        for (const id of params.organizationFileIds) {
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          loadingStates.set(id, {
            ...currentState,
            isLoadingDelete: false,
            deleteError: "Failed to delete file",
          });
        }
        return { fileLoadingStates: loadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.fileLoadingStates);
        for (const id of params.organizationFileIds) {
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          loadingStates.set(id, {
            ...currentState,
            isLoadingDelete: false,
            deleteError: "Failed to delete file",
          });
        }
        return { fileLoadingStates: loadingStates };
      });
      return false;
    }
  },
}));
